import React, { useEffect, useState } from "react";
import Error404 from "./Error404";
import Loader from "../components/Loader";
import OwlCarousel from "react-owl-carousel";
import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";
import { Helmet } from "react-helmet";
import Newsletter from "../components/Newsletter";
import brandsData from "../data/brands.json";
import parse from "html-react-parser";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../components/Breadcrumbs";
import ProductCard from "../components/ProductCard";
import { useQuery } from "@tanstack/react-query";
import { fetchProducts } from "../lib/medusa";
import StructuredData from "../seo/StructuredData";
import { breadcrumbJsonLd } from "../seo/jsonld";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "../seo/site";

const MAX_FEATURED_PRODUCTS = 8;

export default function Brands(props) {
  const { slug } = useParams();
  if (brandsData[slug]?.hidden) {
    return <><Helmet><meta name="robots" content="noindex, nofollow" /></Helmet><Error404 /></>;
  }
  return <PublishedBrand {...props} />;
}

function PublishedBrand({setHeader}) {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [currentBrand, setCurrentBrand] = useState(brandsData[slug]);
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  const pageLang = i18n?.language === "me" ? "me" : "en";
  const pageUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = currentBrand
    ? `${t(currentBrand.title)} - ${SITE_NAME}`
    : SITE_NAME;
  const pageDescription = t("header.brands.metaDescription");
  const enUrl = `${SITE_URL}/en/brands/${slug}`;
  const meUrl = `${SITE_URL}/me/brendovi/${slug}`;
  const ogImage = currentBrand?.heroImg
    ? currentBrand.heroImg.startsWith("http")
      ? currentBrand.heroImg
      : `${SITE_URL}${currentBrand.heroImg}`
    : currentBrand?.logo
      ? `${SITE_URL}${currentBrand.logo}`
      : DEFAULT_OG_IMAGE;
  const breadcrumbItems = [
    { name: SITE_NAME, item: `${SITE_URL}/${pageLang}` },
    {
      name: t("header.brands.pageTitle"),
      item:
        pageLang === "en" ? `${SITE_URL}/en/brands` : `${SITE_URL}/me/brendovi`,
    },
    currentBrand ? { name: t(currentBrand.title), item: pageUrl } : null,
  ].filter(Boolean);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["brand-featured-products", slug],
    queryFn: () => fetchProducts(slug, "created_at", MAX_FEATURED_PRODUCTS, 0),
  });
  const featuredProducts = data?.[0] ?? [];
  const count = data?.[1] ?? 0;
  const featuredProductsCount = featuredProducts.length;
  const shouldLoopFeaturedProducts = featuredProductsCount > 4;

  useEffect(() => {
    const handleScroll = () => {
      const bottomOffset = 400;
      const reachedNearBottom =
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - bottomOffset;

      setIsOverlayVisible(!reachedNearBottom);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setCurrentBrand(brandsData[slug]);
  }, [slug]);

  useEffect(() => {
    if(data)
    {
      const carouselProducts = data[0].slice(0, MAX_FEATURED_PRODUCTS);
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: "view_item_list",
        ecommerce: {
          item_list_id: "related_products",
          item_list_name: "Related products",
          items: carouselProducts.map((product, index) => ({
            item_id: product.id,
            item_name: product.title,
            affiliation: "DURO",
            index: index,
            item_brand: product?.collection?.title,
            item_category: product?.categories?.[0]?.name,
            item_list_id: "related_products",
            item_list_name: "Related products",
            item_variant: product?.variants?.[0]?.title,
            location_id: "",
            price: product.variants?.[0]?.prices?.[0]?.amount,
            quantity: product.variants?.[0]?.inventory_quantity,
          })),
        },
      });
    }
  },[data])

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 1) {
        setHeader(true);
      } else {
        setHeader(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [setHeader]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: pageLang === "me" ? "sr-ME" : "en" }}>
        {/* <!-- meta tag --> */}
        <meta charSet="utf-8" />
        <title>
          {pageTitle}
        </title>

        {/* <!-- Meta Tags--> */}
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="durō, duro, niche perfumery, niche parfimerija,  parfimerija, galerija"
        />
        <meta name="author" content="intellect.se" />
        <link rel="canonical" href={pageUrl} />
        <link rel="alternate" href={enUrl} hrefLang="en" />
        <link rel="alternate" href={meUrl} hrefLang="sr-ME" />
        <link rel="alternate" href={meUrl} hrefLang="x-default" />

        {/* <!-- responsive tag --> */}
        <meta http-equiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* <!-- Facebook Metadata --> */}
        <meta property="og:type" content="website" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <Loader />
      {/* // <!-- Main content Start --> */}
      <div className="main-content">
        {/* <!-- Breadcrumbs Start --> */}
        <div
          className="rs-breadcrumbs electimuss-3"
          style={{
            backgroundImage: `url(${
              currentBrand.heroImg === ""
                ? "/assets/images/breadcrumbs/inr-2.jpg"
                : currentBrand.heroImg
            })`,
          }}
        >
          <div className="container">
            <div className="breadcrumbs-inner">
              <h1 className="page-title">
                {currentBrand.title}
                {/*<span className="watermark">{currentBrand.title}</span>*/}
              </h1>
            </div>
          </div>
        </div>
        <Breadcrumbs bg={undefined} />
        {/* <!-- Breadcrumbs End --> */}

        {/* <!-- Intro Section Start --> */}
        <div className="rs-team style7 lion-bg height-ctrl md-pb-30 md-pt-30">
          {/* <!-- Overlay Shop Start --> */}
          <div
            className="container container-fixwidth"
            style={{ display: isOverlayVisible ? "block" : "none" }}
          >
            <div className="collection-overlay gray-bg4">
              <p className="collection-overlay-text">
                {count ?? featuredProductsCount} {t("brands.collectionProducts")}
              </p>
              {/* <!-- Shop online --> */}
              <div class="btn-online">
                <Link 
                    className="buy-online-button"
                      to={`/${i18n.language}/${
                        i18n.language === "en" ? "brands" : "brendovi"
                      }/${slug}/${
                        i18n.language === "en" ? "products" : "proizvodi"
                      }`}
                    >
                  <span className="btn-txt">{t("brands.allProducts")}</span>
                  <i className="fa fa-cart-plus"></i>
                </Link>
              </div>
            </div>
          </div>
          {/* <!-- Overlay Shop End --> */}
          <div className="container">
            <div className="row y-middle">
              <div className="col-lg-4 pr-15 md-pr-15 md-mb-50">
                <div className="brand-logo">
                  {currentBrand.logo ? (
                    <img src={currentBrand.logo} alt={currentBrand.title} />
                  ) : (
                    <h2 className="title pb-0">{currentBrand.title}</h2>
                  )}
                </div>
              </div>
              <div className="col-lg-8 pl-45 md-pl-15">
                {currentBrand.lead === "" ? (
                  <p className="desc margin-0 pb-15">Coming soon...</p>
                ) : (
                  parse(t(currentBrand.lead))
                )}
              </div>
            </div>
          </div>
        </div>
        {/* <!-- Intro Section End --> */}

        {/* <!-- Products Section Start --> */}
        {isLoading && (
          <div className="loading-text lion-bg">Loading...</div>
        )}
        {isError && (
          <div className="loading-text lion-bg">
            {t("errors.products", { defaultValue: "Proizvodi trenutno nisu dostupni. Pokušajte ponovo." })}
          </div>
        )}
        {featuredProductsCount > 0 ? (
          <div className="rs-team style7 lion-bg height-ctrl md-pb-20 md-pt-20">
            <div className="container">
              <div className="sec-title spotlight text-center mb-30">
                <span className="sub-text">
                  {t("brands.pageHeadings.spotlight")}
                </span>
                <h2 className="title pb-22">
                  {t("brands.pageHeadings.featured")}
                </h2>
                <div className="heading-border-line center-style"></div>
              </div>
              <OwlCarousel
                className="rs-carousel owl-carousel"
                loop={shouldLoopFeaturedProducts}
                items={Math.min(4, featuredProductsCount)}
                margin={30}
                autoplay={shouldLoopFeaturedProducts}
                autoplayHoverPause={true}
                autoplayTimeout={5000}
                smartSpeed={800}
                dots={true}
                nav={true}
                navSpeed={false}
                center={false}
                navText={["<i className='flaticon-left-arrow'></i>","<i className='flaticon-right-arrow'></i>"]}
                responsive={{
                  0: { items: 1, nav: false, dots: true, center: false },
                  768: { items: Math.min(2, featuredProductsCount), nav: false, dots: true, center: false },
                  992: { items: Math.min(3, featuredProductsCount), nav: false, dots: true, center: false },
                  1200: { items: Math.min(4, featuredProductsCount), nav: false, dots: true, center: false },
                }}
              >
                {featuredProducts.map((product, i) => (
                  <ProductCard product={product} key={i} />
                ))}
              </OwlCarousel>

              {/* <!-- All Products Button --> */}
              <div className="btn-all-prods">
                <Link
                  className="all-products-button"
                  to={`/${i18n.language}/${
                    i18n.language === "en" ? "brands" : "brendovi"
                  }/${slug}/${
                    i18n.language === "en" ? "products" : "proizvodi"
                  }`}
                >
                  {t("brands.allProducts")}
                  <i className="fa fa-cart-plus"></i>
                </Link>
              </div>
            </div>
          </div>
        ) : null}
        {/* <!-- Products Section End --> */}

        {/* <!-- About Section Start --> */}
        <div className="rs-about style1 lion-bg pt-20 pb-40 md-pt-20 md-pb-20">
          <div className="container">
            {currentBrand.story === "" ? null : (
              <div className="row y-middle pt-60 pb-40 md-pt-60 md-pb-20">
                <div className="col-lg-6 md-mb-50">
                  <div className="sec-title mb-60">
                    <span className="sub-text">
                      {t("brands.pageHeadings.about")}
                    </span>
                    <h2 className="title pb-22">
                      {currentBrand.name} <br />
                      {t(currentBrand.tagline)}
                    </h2>
                    <div className="heading-border-line left-style"></div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12">
                      {parse(t(currentBrand.story))}
                    </div>
                  </div>
                </div>
                {currentBrand.storyImg ? (
                  <div className="col-lg-6 pl-50 md-pl-15">
                    <div className="images-part">
                      <img src={currentBrand.storyImg} alt={currentBrand.name} />
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
        {/* <!-- About Section End --> */}

        {/* <!-- Quote Section Start --> */}
        {currentBrand.quote === "" ? null : (
          <div className="rs-team style2 lion-bg pt-50 pb-50 md-pt-50">
            <div className="container">
              <div className="sec-title text-center mb-60">
                <span className="sub-text">
                  {t("brands.pageHeadings.quote")}
                </span>
                <h2 className="title white-color pb-35">
                  "{t(currentBrand.quote)}"
                </h2>
                <div className="heading-border-line"></div>
              </div>
            </div>
          </div>
        )}
        {/* <!-- Quote Section End --> */}

        {/* <!-- Newsletter section start --> */}
        <div className="rs-newsletter lion-bg style1 pt-90 md-pt-60 mb--65">
          <Newsletter />
        </div>

        {/* <!-- Newsletter section end --> */}
      </div>
      {/* // <!-- Main content End --> */}
      <StructuredData data={breadcrumbJsonLd(breadcrumbItems)} />
    </>
  );
}
